import { useEffect, useMemo, useState } from 'react';
import { Bot } from 'lucide-react';
import MentionEditor from '../builder/MentionEditor';
import { buildMentionables } from '../builder/promptMentions';
import useObjectiveMentions from '../builder/useObjectiveMentions';
import useApi from '../../api';
import { AgentAppOverrides } from './appOverrides';
import { AgentSkill } from './types';
import AgentTabHeader from './AgentTabHeader';

export interface AppPromptingTabProps {
    agId?: string;
    appOverrides: AgentAppOverrides;
    setAppOverrides: (updater: (prev: AgentAppOverrides) => AgentAppOverrides) => void;
    form?: any[];
    skills?: AgentSkill[];
}

/**
 * App mode Prompting tab. The base prompt (`goal`) is authored fresh for
 * app mode — it does NOT inherit from the website goal, because in-product
 * prompting is typically different enough that reusing the website prompt
 * would lead to worse answers. Persona is still website-only (shared).
 */
export default function AppPromptingTab({
    agId,
    appOverrides,
    setAppOverrides,
    form,
    skills,
}: AppPromptingTabProps) {
    const value = appOverrides.goal ?? '';

    const { get } = useApi();
    const [organizationUsers, setOrganizationUsers] = useState<any[]>([]);

    // Org users for Team @mentions in the app base prompt editor.
    useEffect(() => {
        let cancelled = false;
        const fetchUsers = async () => {
            try {
                const usersResponse = await get('/users/');
                if (!cancelled) setOrganizationUsers(usersResponse.data || []);
            } catch (error) {
                console.error('Error fetching organization users:', error);
            }
        };
        fetchUsers();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const objectives = useObjectiveMentions(agId);
    const mentionables = useMemo(
        () => buildMentionables(form || [], skills || [], { users: organizationUsers, objectives }),
        [form, skills, organizationUsers, objectives]
    );

    return (
        <div className="w-full">
            <AgentTabHeader
                icon={Bot}
                title="App mode prompting"
                description="Define how your agent behaves when someone is signed in to your product."
            />

            <div className="mx-auto w-full max-w-4xl py-6">
                <section className="space-y-3">
                    <div>
                        <h3 className="text-sm font-semibold">Base prompt</h3>
                        <p className="mt-1 text-xs leading-5 text-muted-foreground">
                            Set app-specific goals, guardrails, and behavior. This prompt does not inherit from website mode.
                        </p>
                    </div>

                    <MentionEditor
                        value={value}
                        onChange={(next) =>
                            setAppOverrides((prev) => {
                                if (next.length === 0) {
                                    const { goal: _omit, ...rest } = prev;
                                    return rest;
                                }
                                return { ...prev, goal: next };
                            })
                        }
                        mentionables={mentionables}
                        placeholder="Enter app mode specific prompt instructions... Type @ to reference fields, skills, tools, or teammates."
                        className="resize-y overflow-auto font-mono [&_.ProseMirror]:h-full [&_.ProseMirror]:min-h-[300px]"
                    />
                </section>
            </div>
        </div>
    );
}
